'use client';

import { PropsWithChildren } from 'react';
import { useRouter } from 'next/navigation';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { datadogRum } from '@datadog/browser-rum';
import { isAuthenticationResult } from '@/lib/msal';
import { CustomNavigationClient } from '@/lib/navigationClient';
import { msalConfig } from '@/authConfig';
import { getCurrentEnvironment, switchEnvironment } from '@/utils/authCache';
import { EventType as DDEventType } from '@/lib/types';

if (!msalConfig.auth.clientId) {
  console.error(
    'ERROR: clientId not set, authenticated features (login, secure API calls) will not work.',
  );
}

const msalInstance = new PublicClientApplication(msalConfig);
let startTime: number | null = null;

msalInstance.initialize().then(() => {
  const currentEnv = getCurrentEnvironment();
  switchEnvironment(currentEnv);
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      isAuthenticationResult(event.payload)
    ) {
      msalInstance.setActiveAccount(event.payload.account);
    }

    if (event.eventType === EventType.HANDLE_REDIRECT_START && !startTime) {
      startTime = Date.now();
    }

    if (!startTime || !window.DD_RUM) return;

    const loginDuration = startTime ? Date.now() - startTime : null;

    if (event.eventType === EventType.LOGIN_FAILURE) {
      datadogRum.addAction('login_failure', {
        error: event.error?.message || 'Login failed',
        eventType: DDEventType.LOGIN,
        loginDuration,
        timestamp: Date.now(),
      });

      startTime = null;
    }

    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      isAuthenticationResult(event.payload)
    ) {
      const account = event.payload.account;

      datadogRum.addAction('login_success', {
        email: account?.username,
        eventType: DDEventType.LOGIN,
        loginDuration,
        timestamp: Date.now(),
        userId: account?.localAccountId,
      });

      startTime = null;
    }
  });
});

export function AuthenticationProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  (window as any).msalInstance = msalInstance;

  const navigationClient = new CustomNavigationClient(router);
  msalInstance.setNavigationClient(navigationClient);

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
